import { Crumb } from '@/components/Breadcrumbs/BreadcrumbWrapper';
import { ItemSummary } from '@/types/item/ItemSummary';
import LandingPageMetadata from '@/types/metadata/LandingPageMetadata';
import { TypedActionWithPayloadMetaActionTime } from '@/types/redux';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { AugmentedSearchQuery, SearchQueryStatus } from '@/types/search/searchQuery';
import { ResultFacet } from '@/types/SearchFacet';
import { QueryIntentFromApi } from '@/utils/amplitude/analytics/core.types';
import { AmplitudePageName } from '@/utils/getPageNameFromPath';

export type FetchCategoryResultsParams = {
    aggsOnly?: boolean;
    authToken: string;
    deployment: string;
    ids: any;
    ipAddress: string;
    searchQuery: any;
    skipAggs?: boolean;
    useScoutCategories?: boolean;
};

/**
 * search-party's `didYouMean` param. `Correct` swaps a zero-result search for the corrected one;
 * `Suggest` returns the suggestion but keeps the original (empty) results.
 */
export enum DidYouMeanMode {
    Correct = 'correct',
    Suggest = 'suggest',
}

/**
 * Present whenever ssa-api returned a spelling suggestion, corrected or not. `correctedQuery` is
 * set only when `isCorrected`, i.e. when the results in the same payload are for it.
 */
export type SpellCorrection = {
    correctedQuery?: string;
    dictionaryVersion: string;
    distance: number;
    isCorrected: boolean;
    originalQuery: string;
    suggestion: string;
};

export type FetchSearchResultsParams = {
    aggsOnly?: boolean;
    authToken: string;
    deployment: string;
    didYouMean?: DidYouMeanMode;
    geoCoordinates: any;
    ipAddress: string;
    popularityScoreReRankingVersion?: string;
    rankingVersionVariant?: string;
    searchQuery: AugmentedSearchQuery;
    skipAggs?: boolean;
    useGeoLocation: boolean;
    useScoutCategories?: boolean;
};

export const DEFAULT_ARCHIVE_STATUSES: SearchQueryStatus[] = [
    SearchQueryStatus.Sold,
    SearchQueryStatus.Passed,
    SearchQueryStatus.Done,
];
export const DEFAULT_LIVE_STATUSES: SearchQueryStatus[] = [
    SearchQueryStatus.Upcoming,
    SearchQueryStatus.Live,
    SearchQueryStatus.Online,
];
export const DEFAULT_SORT_ORDER = SearchSortAndDirection.MostRelevant;

export enum ElasticAggregateDemandStatus {
    Error = 'error',
    Idle = 'idle',
    Loaded = 'loaded',
    Loading = 'loading',
}

export type SearchSlice = {
    /** Tracks on-demand aggregation fetching: 'idle' | 'loading' | 'loaded' | 'error' */
    aggregationsOnDemandStatus: ElasticAggregateDemandStatus;
    /**
     * Status of the AuctionHouse search-as-you-type fetch. Tracked separately
     * from `aggregationsOnDemandStatus` because that flag drives a full-drawer
     * `<Loading />` overlay in `AllFilters.tsx` — appropriate for the one-shot
     * skipAggs hydration but disastrous for per-keystroke typeahead UX. The
     * AuctionHouseFilter reads this field for its in-section spinner only.
     */
    auctionHouseTypeaheadStatus: ElasticAggregateDemandStatus;
    categoryCrumbs: Crumb[];
    currentQueryKey?: string;
    /**
     * Set when the deep-page search/category request came back 403 (search-party's
     * gate on deep pagination for signed-out bidders). Distinct from `error` so the
     * page shell, SEO meta, and breadcrumbs stay intact behind a sign-in interstitial
     * instead of falling back to the generic `SearchError` technical-difficulties UI.
     */
    deepPageRequiresAuth: boolean;
    error: boolean;
    facets: any[];
    isInitialLoading: boolean;
    isLoading: boolean;
    isZeroResults: boolean;
    itemIds: number[];
    itemScores?: Record<string, number>;
    /**
     * Dominant L1/L2 category of the current keyword session, persisted so the
     * cached-result Search Completed event (identical re-search, no fresh
     * response to read) can still report it. Sticky across same-session
     * refinements — a filtered query gates the backend meta off, so absence is
     * treated as "keep" within a session and "clear" only on a new keyword.
     */
    dominantCategory?: DominantCategory;
    /**
     * Facet IDs to render as quick pills for the current response. Empty when
     * the backend has no rule match.
     */
    priorityFacets: string[];
    /**
     * Last non-empty `priorityFacets` value captured for this query session.
     * Lets the FE keep showing the pills while the user refines a keyword
     * search — on filtered queries the backend's meta primitive is gated off
     * and returns `[]`, but the user is still inside the same query intent.
     * Reset by `addSearchResults` when `stickyTermAnchor` changes (new keyword).
     */
    stickyPriorityFacets: string[];
    /**
     * The `seoSearchTerm.term` value from the last fulfilled search response,
     * used by sticky-pills logic to detect a session boundary. Held separate
     * from `seoSearchTerm` because `resetSearchState` clears `seoSearchTerm`
     * on every `.pending` — sharing that field would make sticky reset on
     * every refinement instead of only on a new keyword.
     */
    stickyTermAnchor?: string;
    lastSearchFetchOptions?: FetchSearchResultsParams | (FetchCategoryResultsParams & { isCategorySearch: true });
    queryModifications?: QueryModifications;
    searchInputBuffer: string;
    sellerItemsSearch?: boolean;
    sellerItemsSearchSellerId?: number;
    seoSearchTerm?: {
        indexable?: boolean; // seo-dbi slugs only: false means keep the page out of the index
        metaDescription: string;
        pageTitle: string; // <h1>
        relatedTerms: {
            label: string;
            slug: string;
            url: string;
        }[];
        seoCopy: string;
        seoTitle: string;
        tabName: string; // <title>
        term: string; // Clean search term from API
    };
    similarItemsURL?: string;
    soldItemFacets: any[] | null;
    soldItemIds: number[];
    sort: SearchSortAndDirection[];
    spellCorrection?: SpellCorrection;
    submitted: boolean;
    submittedFromPage: AmplitudePageName;
    success: boolean;
    totalBuyNowItems: number;
    totalFound: number;
    totalLiveItems: number;
    totalPages: number;
    totalSoldItems: number;
    useGeoLocation: boolean;
};

export const defaultSearchSlice: SearchSlice = {
    aggregationsOnDemandStatus: ElasticAggregateDemandStatus.Idle,
    auctionHouseTypeaheadStatus: ElasticAggregateDemandStatus.Idle,
    categoryCrumbs: [],
    currentQueryKey: '',
    deepPageRequiresAuth: false,
    error: false,
    facets: [],
    isInitialLoading: true,
    isLoading: true,
    isZeroResults: false,
    itemIds: [],
    priorityFacets: [],
    stickyPriorityFacets: [],
    stickyTermAnchor: undefined,
    searchInputBuffer: '',
    sellerItemsSearch: false,
    sellerItemsSearchSellerId: 0,
    seoSearchTerm: undefined,
    similarItemsURL: '',
    soldItemFacets: [],
    soldItemIds: [],
    sort: [],
    spellCorrection: undefined,
    submitted: false,
    submittedFromPage: undefined,
    success: false,
    totalBuyNowItems: 0,
    totalFound: 0,
    totalLiveItems: 0,
    totalPages: 0,
    totalSoldItems: 0,
    useGeoLocation: false,
};

export const SAVE_LAST_SEARCH_FETCH_OPTIONS = 'la/domain/search/SAVE_LAST_SEARCH_FETCH_OPTIONS';

/**
 * @deprecated If using redux-toolkit's createSlice, use submitSearch.fulfilled
 */
export const LOAD_SEARCH_RESULTS_SUCCESS = 'la/domain/searchResults/LOAD_SUCCESS';
/**
 * @deprecated If using redux-toolkit's createSlice, use submitSearch.fulfilled
 */
export type LOAD_SEARCH_RESULTS_SUCCESS_ACTION = TypedActionWithPayloadMetaActionTime<
    typeof LOAD_SEARCH_RESULTS_SUCCESS,
    SearchResultPayload
>;

export type SubmitSearchPayload = {
    didYouMean?: DidYouMeanMode;
    pageName: string;
    popularityScoreReRankingVersion?: string;
    rankingVersionVariant?: string;
    routerState?: {
        [key: string]: any;
    };
    searchQuery: any;
    skipAggs?: boolean;
    useScoutCategories?: boolean;
};

export type QueryRewriteConfigUsed = {
    duplicateNamesHandling: string;
    enabled: boolean;
    entities: string[] | null;
    entitiesPrefixOnly: boolean;
    exactMatchOnly: boolean;
    keepHouseInQueryOnExactMatch: boolean;
};

export type QueryRewriteFilterApplied = {
    field: string;
    houseNames: string[] | null;
    type: string;
    values: string[] | null;
};

export type QueryRewrite = {
    applied: boolean;
    configUsed: QueryRewriteConfigUsed;
    filterApplied: QueryRewriteFilterApplied;
    housesCount: number;
    matchedHouses: string[] | null;
    modifiedQuery: string;
    normalizedHouseNames: string[] | null;
    originalQuery: string;
    queryIntent?: keyof typeof QueryIntentFromApi;
    rewriteType: string;
    status: string;
};

export type QueryModifications = {
    queryRewrite: QueryRewrite;
};

/**
 * Dominant L1/L2 category labels for a keyword search whose results are
 * concentrated in one category (search-party emits it when the top bucket's
 * share exceeds the dominance threshold). Absent for browse/category searches
 * and when no category dominates. `l2` is present only when an L2 dominates.
 */
export type DominantCategory = {
    l1: string;
    l2?: string;
};

/**
 * SearchResultPayload - search payload result shape for most search-party search results
 * @category Search API
 * @see SearchResult
 */
export type SearchResultPayload = {
    currentQueryKey?: string;
    dominantCategory?: DominantCategory;
    // `null` on skipAggs responses; the reducer treats that as "keep existing facets" so a
    // late-arriving skipAggs response doesn't clobber a parallel on-demand aggregation fetch.
    facets: ResultFacet[] | null;
    itemIDs: number[];
    items: ItemSummary[];
    itemScores?: Record<string, number>;
    landingPageMetadata: LandingPageMetadata[];
    pageCount: number;
    /**
     * Ordered list of facet IDs to render first as quick pills. Optional on
     * the wire — the reducer treats absent / `null` / `[]` identically
     * (falls back to the default pill row).
     */
    priorityFacets?: string[];
    queryModifications?: QueryModifications;
    seoSearchTerm?: {
        h1Header: string;
        /**
         * Only on slugs resolved by seo-service (ff_sear_2174_seo_dbi_slug_page); absent for legacy SEO terms.
         * search-party upgrades false to true when the search returns enough results to be worth indexing.
         */
        indexable?: boolean;
        metaDescription: string;
        pageTitle: string;
        relatedTerms: {
            label: string;
            slug: string;
            url: string;
        }[];
        seoCopy: string;
        seoTitle: string;
        term: string;
    };
    similarItemsURL?: string;
    soldItemFacets: ResultFacet[] | null;
    soldItemIDs: number[] | null;
    sort: SearchSortAndDirection[];
    spellCorrection?: SpellCorrection;
    startIndex: number;
    time: string;
    totalBuyNow: number;
    totalFound: number;
    totalLive?: number;
    totalPages: number;
    totalRecords?: number;
    totalSold?: number;
};

export type CategoryBreadcrumb = {
    id: string;
    name: string;
    url: string;
};

/**
 * CategorySearchResultPayload - search payload result shape for v4/categoryweb results.
 * @category Search API
 * @see CategorySearchResult
 */
export type CategorySearchResultPayload = SearchResultPayload & {
    breadcrumbs: CategoryBreadcrumb[];
};

/**
 * Represents a valid search response state.
 */
export type ValidSearchResult<SearchPayload> = {
    error: false;
    payload: SearchPayload;
};

/**
 * Represents an invalid search response state.
 */
export type ErrorSearchResult = {
    error: true;
    payload: string;
};

export type BaseSearchResult<SearchPayload> = ValidSearchResult<SearchPayload> | ErrorSearchResult;

/**
 * @category Search API
 * @see fetchPromotedSearchResults
 * @see fetchSearchResults
 * @see SearchResultPayload
 */
export type SearchResult = BaseSearchResult<SearchResultPayload>;

/**
 * @category Search API
 * @see fetchCategoryResults
 */
export type CategorySearchResult = BaseSearchResult<CategorySearchResultPayload>;

/**
 * User type guard function, checks if our response is an error or not.
 * @param resp Either a SearchResult or a CategorySearchResult
 * @returns true if in error state.
 */
export const isErrorResponse = (resp: SearchResult | CategorySearchResult): resp is ErrorSearchResult => {
    return resp.error === true || typeof resp.payload === 'string';
};
